import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Rating
} from '@mui/material'
import {
  Favorite as FavoriteIcon,
} from '@mui/icons-material'
import { useGetCigarDetailQuery, useAddToWishlistMutation } from '../../store/api/cigarDetailApi'

const CigarDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const {
    data: cigar,
    isLoading,
    error
  } = useGetCigarDetailQuery(id || '')
  
  const [addToWishlist] = useAddToWishlistMutation()
  
  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading cigar details...</Typography>
        </Box>
      </Container>
    )
  }
  
  if (error || !cigar) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Error</Typography>
          <Typography>
            {error && 'status' in error ? 'Failed to load cigar details' : 'Cigar not found'}
          </Typography>
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/database')}>
            Back to Database
          </Button>
        </Box>
      </Container>
    )
  }
  
  const handleAddToWishlist = () => {
    addToWishlist({ cigarId: id! })
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link component="button" onClick={() => navigate('/database')} sx={{ mr: 1 }}>
          Database
        </Link>
        <Typography color="text.primary">{cigar.name}</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {cigar.name}
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {cigar.brand?.name} • {cigar.cigarLine?.name}
        </Typography>
        
        {/* Rating */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Rating 
            value={cigar.stats?.averageRating || 0} 
            precision={0.5} 
            readOnly 
            size="large"
          />
          <Typography variant="body2" color="text.secondary">
            ({cigar.stats?.averageRating || 0} • {cigar.stats?.totalReviews || 0} reviews)
          </Typography>
        </Box>
        
        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FavoriteIcon />}
            onClick={handleAddToWishlist}
          >
            Wishlist
          </Button>
        </Box>
      </Box>
      
      {/* Image Gallery */}
      {cigar.images && cigar.images.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Images ({cigar.images.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {cigar.images.map((image, index) => (
                <Box
                  key={index}
                  component="img"
                  src={image}
                  alt={`Cigar image ${index + 1}`}
                  sx={{
                    width: 200,
                    height: 150,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flexShrink: 0,
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s'
                    }
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Specifications */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Specifications
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Length
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.length}&quot;
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Ring Gauge
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.ringGauge}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Shape
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.shape || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Strength
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.strengthLevel || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Wrapper
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.wrapper || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Binder
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.binder || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Filler
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.filler || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Manufacturer Information */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Manufacturer Information
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Manufacturer
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.manufacturer || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Country
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.country || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Region
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.region || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Factory
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.factory || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Community Reviews */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Community Reviews
          </Typography>
          {cigar.reviews && cigar.reviews.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {cigar.reviews.slice(0, 3).map((review) => (
                <Box key={review.id} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      {review.user?.firstName || 'Anonymous'}
                    </Typography>
                    <Rating value={review.overallRating} precision={0.5} readOnly size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {review.reviewText}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              ))}
              {cigar.reviews.length > 3 && (
                <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                  View all {cigar.reviews.length} reviews
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reviews yet. Be the first to review this cigar!
            </Typography>
          )}
        </CardContent>
      </Card>
      
      {/* Tasting Notes */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tasting Notes
          </Typography>
          <Typography variant="body1">
            {cigar.tastingNotes || 'No tasting notes available'}
          </Typography>
        </CardContent>
      </Card>
      
      {/* Availability */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Availability & Pricing
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Availability
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.availability || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Price Range
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {cigar.priceRange || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" color="text.secondary">
                Average Price
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                ${cigar.averagePrice ? `$${cigar.averagePrice.toFixed(2)}` : 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Similar Cigars */}
      {cigar.similarCigars && cigar.similarCigars.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Similar Cigars
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {cigar.similarCigars.map((similar) => (
                <Box key={similar.id} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1">
                    {similar.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {similar.brand?.name} • {similar.cigarLine?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Rating value={similar.stats?.averageRating || 0} precision={0.5} readOnly size="small" />
                    <Typography variant="caption" color="text.secondary">
                      ({similar.stats?.averageRating || 0} • {similar.stats?.totalReviews || 0} reviews)
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  )
}

export default CigarDetail
